import {computed, defineComponent, onMounted, ref, watch} from "vue";
import * as KlipApi from "@/api/klip-api.proxy";
import {IAlertNotification, MessageType} from '@/api/klip-api.proxy';
import {IpproDatatable} from "@/app/shared/components/ippro-datatable/ippro-datatable-declare";
import DateUtil from '@/app/shared/helpers/date-util';
import {useKlipApiProxy} from '@/plugins/proxy-client';
import {useForm} from 'vee-validate';
import {useKlModalEventsUtil} from '@/app/shared/helpers/kl-modal-events-util';
import {first} from 'lodash-es';
import {htmlEncode} from 'js-htmlencode';
import * as yup from 'yup';
import {toTypedSchema} from '@vee-validate/yup';
import {
    klValidationFields,
    ValidationFieldsType
} from '@/app/shared/components/kl-form-fields/kl-field-validation-util';
import DOMPurify from 'dompurify';
export interface IMessageTypeMetaData {
    type: KlipApi.MessageType;
    translation: string,
    icon: string,
    modType: string,
    isClosable: boolean
}

export default defineComponent({
    compatConfig: {
        MODE: 3,
    },
    name: 'KlAdminMessages',
    setup() {

        const schema = yup.object({
            startDates: yup.array().required().label('Vanaf'),
            endDates: yup.array().notRequired().label('Tot en met'),
            title: yup.string().notRequired().label('Titel'),
            text: yup.string().required().label('Inhoud'),
        });

        const messageModalId = 'message-modal';

        const proxy = useKlipApiProxy();

        const messageModalEvents = useKlModalEventsUtil(messageModalId, {
            onClosed() {
                resetMessage();
            },
        });


        const form = useForm({
            validationSchema: toTypedSchema(schema),
        });
        const fields: ValidationFieldsType<typeof schema> = klValidationFields<typeof schema>(schema);

        const message = ref<KlipApi.UpdateMessageRequest & { startDates: string[], endDates: string[] }>(null);
        watch(() => message.value?.startDates, (newValue) => {
            fields.startDates.value.value = newValue;
        });
        watch(() => message.value?.endDates, (newValue) => {
            fields.endDates.value.value = newValue;
        });
        watch(() => message.value?.title, (newValue) => {
            fields.title.value.value = newValue;
        });
        watch(() => message.value?.text, (newValue) => {
            fields.text.value.value = newValue;
        });


        const saving = ref<boolean>(false);
        const deleting = ref<boolean>(false);
        const isDataTableLoading = ref<boolean>(true);

        const messages = ref<KlipApi.MessageDto[]>([]);
        const meta = ref<{ totalRows: number, resultsPerPage: number, currentPage: number | undefined }>({
            totalRows: 1,
            resultsPerPage: 1,
            currentPage: 1
        });
        const today = DateUtil.formatDateTime(DateUtil.today(), 'DD/MM/YYYY HH:mm');
        const activationMinDate = ref<string>(today);

        const typeMetadata: Record<KlipApi.MessageType, IMessageTypeMetaData> = {
            [MessageType.Announcement]: {
                type: KlipApi.MessageType.Announcement,
                translation: "Aankondiging",
                icon: "info-circle",
                modType: "mod-info",
                isClosable: true
            },
            [MessageType.Warning]: {
                type: KlipApi.MessageType.Warning,
                translation: "Opgelet",
                icon: "warning",
                modType: "mod-warning",
                isClosable: false
            },
            [MessageType.WhatsNew]: {
                type: KlipApi.MessageType.WhatsNew,
                translation: "Nieuw",
                icon: null,
                modType: null,
                isClosable: false
            }
        };

        const cellParser = (row: IRow, column: IColumn) => {
            let rowTemplate: string = `<div>${!!row[column.key] ? row[column.key] : ''}</div>`;
            switch (column.key) {
                case 'type': {
                    rowTemplate = `<div>${typeMetadata[row.type]?.translation}</div>`;
                    break;
                }
                case 'period': {
                    if (!!row.endTimestamp) {
                        rowTemplate = `<div>${DateUtil.formatDate(row.startTimestamp)} tot en met ${DateUtil.formatDate(row.endTimestamp)}</div>`;
                    } else {
                        rowTemplate = `<div>${DateUtil.formatDate(row.startTimestamp)}</div>`;
                    }

                    break;
                }
                case 'text': {
                    rowTemplate = `<div>${htmlEncode(row.text)}</div>`;
                    break;
                }
            }

            return {
                template: rowTemplate,
            };
        };

        const columns = ref<IColumn[]>([
            {
                key: 'type',
                label: 'Type',
                sortable: false,
                isVisible: true,
                width: 120,
                widthUnit: "px",
                parser: cellParser
            },
            {
                key: 'title',
                label: 'Titel',
                sortable: false,
                isVisible: true,
                width: 20,
                widthUnit: "em",
                parser: cellParser
            },
            {
                key: 'text',
                label: 'Inhoud',
                sortable: false,
                isVisible: true,
                width: 35,
                widthUnit: "em",
                parser: cellParser
            },
            {
                key: 'period',
                label: 'Periode',
                sortable: false,
                isVisible: true,
                parser: cellParser,
                width: 150,
                widthUnit: "px"
            }
        ]);

        const isNewMessage = computed(() => {
            return !message.value.messageId || message.value.messageId <= 0;
        });

        const isWhatsNew = computed(() => {
            return message.value && message.value.type === KlipApi.MessageType.WhatsNew;
        });

        const isHeaderMessage = computed(() => {
            return message.value && (message.value.type === KlipApi.MessageType.Announcement || message.value.type === KlipApi.MessageType.Warning);
        });

        const headerMessages = computed(() => {
            if (!isHeaderMessage.value) return [];

            return [
                <IAlertNotification>{
                    key: message.value.messageId,
                    icon: typeMetadata[message.value.type].icon,
                    isClosable: typeMetadata[message.value.type].isClosable,
                    type: typeMetadata[message.value.type].modType,
                    title: message.value.title,
                    message: sanitizedMessage(message.value.text)
                }
            ];
        });

        const loadMessages = async () => {
            const response = await proxy.operations_Messages();
            if (response.isSuccessStatusCode) {
                messages.value = response.result;

                meta.value.currentPage = 1;
                meta.value.resultsPerPage = response.result.length;
                meta.value.totalRows = response.result.length;

                isDataTableLoading.value = false;
            }
        };

        const rowClicked = async (row: IpproDatatable.IRow) => {
            message.value = <KlipApi.UpdateMessageRequest & { startDates: string[], endDates: string[] }>{
                messageId: row.messageId,
                type: row.type,
                startTimestamp: row.startTimestamp,
                startDates: [row.startTimestamp],
                endTimestamp: row.endTimestamp,
                endDates: !!row.endTimestamp ? [row.endTimestamp] : [],
                title: row.title,
                text: row.text
            };
            messageModalEvents.triggerModalToggle();
        };

        const newMessage = async () => {
            resetMessage();
            messageModalEvents.triggerModalToggle();
        };

        const resetMessage = () => {
            message.value = <KlipApi.UpdateMessageRequest & { startDates: string[], endDates: string[] }>{
                messageId: null,
                type: KlipApi.MessageType.Announcement,
                startTimestamp: today,
                startDates: [today],
                endTimestamp: null,
                endDates: null,
                title: null,
                text: null
            };

            form.resetForm({values: message.value});
        };

        const sanitizedMessage = (message: string) => {
            return DOMPurify.sanitize(message);
        }

        const _saveMessage = async () => {
            saving.value = true;
            message.value.text = sanitizedMessage(message.value.text);
            message.value.startTimestamp = DateUtil.formatForApi(first(message.value.startDates), 'YYYY-MM-DD 00:00:00');
            message.value.endTimestamp = DateUtil.formatForApi(first(message.value.endDates), 'YYYY-MM-DD 00:00:00');
            await proxy.operations_UpdateMessage(message.value);
            saving.value = false;

            messageModalEvents.triggerModalToggle();

            resetMessage();
            await loadMessages();
        };

        const deleteMessage = async () => {
            deleting.value = true;
            await proxy.operations_DeleteMessage(message.value.messageId);
            deleting.value = false;

            messageModalEvents.triggerModalToggle();


            resetMessage();
            await loadMessages();
        };

        onMounted(async () => {
            resetMessage();
            await loadMessages();
        });

        const _onInvalidSubmit = (submittedValues) => {
            console.log('_onInvalidSubmit', submittedValues);
        }

        const onSubmit = form.handleSubmit(_saveMessage, _onInvalidSubmit);

        return {
            isDataTableLoading,
            saving,
            cellParser,
            columns,
            meta,
            messages,
            message,
            activationMinDate,
            rowClicked,
            messageModalId,
            typeMetadata,
            newMessage,
            deleting,
            deleteMessage,
            isNewMessage,
            isWhatsNew,
            isHeaderMessage,
            headerMessages,

            sanitizedMessage,
            onSubmit,

            fields,
        };
    }
});
